/**
 * Header behaviour.
 *  <sticky-header data-sticky="none|always|scroll_up"> — sticky state
 *    classes (is-stuck, is-hidden) and the --header-height variable.
 *  <disclosure-menu> — wraps a <details>: hover intent on fine pointers,
 *    Escape, close on outside click / focus leaving, one open at a time,
 *    and optional category panes ([data-mega-tab] → [data-mega-pane]).
 */
(() => {
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'none';
      this.lastY = window.scrollY;
      this.ticking = false;
      this.setHeight();
      if ('ResizeObserver' in window) new ResizeObserver(() => this.setHeight()).observe(this);
      window.addEventListener('resize', () => this.setBottom());
      if (this.mode === 'none') return;
      this.phone = window.matchMedia('(max-width: 56.24rem)');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('menu:open', () => this.classList.remove('is-hidden'));
      document.addEventListener('drawer:open', () => this.classList.remove('is-hidden'));
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    setHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      this.setBottom();
    }

    /* Bottom edge of the header in the viewport (announcement bar included while
     * it is visible): the mega panel and its scrim hang from it. */
    setBottom() {
      const bottom = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.setBottom();
        this.ticking = false;
      });
    }

    /* Reference: compact after 100px; the header tucks away on scroll-down
     * only on phones (past 460px) and returns on scroll-up. */
    update() {
      const y = window.scrollY;
      if (y > 100) this.classList.add('is-stuck');
      else if (y < 24) this.classList.remove('is-stuck');

      if (this.mode === 'scroll_up') {
        const delta = y - this.lastY;
        const menuOpen = this.querySelector('details[open]');
        const phone = this.phone.matches;
        if (phone && delta > 4 && y > 460 && !menuOpen) this.classList.add('is-hidden');
        else if (!phone || delta < -4 || y <= 100) this.classList.remove('is-hidden');
      }
      this.lastY = y;
    }
  }

  class DisclosureMenu extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      if (this.hasAttribute('data-hover-intent') && finePointer.matches) {
        const openDelay = Number(this.dataset.openDelay ?? 120);
        const closeDelay = Number(this.dataset.closeDelay ?? 240);
        this.addEventListener('mouseenter', () => {
          window.clearTimeout(this.timer);
          this.timer = window.setTimeout(() => this.open(), openDelay);
        });
        this.addEventListener('mouseleave', () => {
          window.clearTimeout(this.timer);
          this.timer = window.setTimeout(() => this.close(), closeDelay);
        });
      }
      this.querySelectorAll('[data-mega-close]').forEach((el) => el.addEventListener('click', () => {
        this.close();
        this.summary.focus();
      }));

      this.details.addEventListener('toggle', () => {
        if (this.details.open) {
          DisclosureMenu.closeOthers(this);
          this.dispatchEvent(new CustomEvent('menu:open', { bubbles: true }));
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          event.preventDefault();
          this.close();
          this.summary.focus();
        }
      });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
      document.addEventListener('click', (event) => {
        if (this.details.open && !this.contains(event.target)) this.close();
      });
      document.addEventListener('drawer:open', () => this.close());
      document.addEventListener('predictive:open', () => this.close());

      this.tabs = Array.from(this.querySelectorAll('[data-mega-tab]'));
      this.panes = Array.from(this.querySelectorAll('[data-mega-pane]'));
      this.tabs.forEach((tab) => {
        const activate = () => this.activate(tab);
        tab.addEventListener('mouseenter', activate);
        tab.addEventListener('focus', activate);
        tab.addEventListener('click', (event) => {
          // Coarse pointers: first tap reveals the pane, second tap follows the link.
          if (!finePointer.matches && tab.getAttribute('aria-current') !== 'true') {
            event.preventDefault();
            activate();
          }
        });
      });
    }

    activate(tab) {
      const id = tab.dataset.megaTab;
      this.tabs.forEach((t) => {
        if (t === tab) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      // Content panes and aside (feature) panes share the same index.
      this.panes.forEach((pane) => {
        pane.hidden = pane.dataset.megaPane !== id;
      });
    }

    open() {
      this.details.open = true;
    }

    close() {
      this.details.open = false;
    }

    static closeOthers(current) {
      document.querySelectorAll('disclosure-menu').forEach((menu) => {
        if (menu !== current && typeof menu.close === 'function') menu.close();
      });
    }
  }

  /* Mobile categories: one open room at a time (reference). */
  document.querySelectorAll('[data-accordion-exclusive]').forEach((nav) => {
    nav.addEventListener('toggle', (event) => {
      const opened = event.target;
      if (!opened.open || opened.closest('[data-accordion-exclusive]') !== nav) return;
      nav.querySelectorAll('details[open]').forEach((other) => {
        if (other !== opened && !other.contains(opened) && !opened.contains(other)) other.open = false;
      });
    }, true);
  });

  /* Rotating search prompts ([data-typing-prompts]): the placeholder types
   * each prompt, holds it, deletes it and moves on, only while the field is
   * empty and unfocused and never for reduced motion. The real placeholder
   * returns on focus. */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phoneWidth = window.matchMedia('(max-width: 56.24rem)');
  document.querySelectorAll('input[data-typing-prompts]').forEach((input) => {
    let prompts = [];
    try {
      prompts = JSON.parse(input.dataset.typingPrompts).filter(Boolean);
    } catch {
      prompts = [];
    }
    if (!prompts.length || reduceMotion.matches) return;
    const original = input.placeholder;
    let index = 0;
    let chars = 0;
    let deleting = false;
    let timer = 0;
    /* Phones only, as in the design reference: desktop keeps the full
     * placeholder, which already fits. */
    const idle = () => phoneWidth.matches && document.activeElement !== input && !input.value;
    const tick = () => {
      if (!idle()) {
        input.placeholder = original;
        return;
      }
      const prompt = prompts[index];
      chars += deleting ? -1 : 1;
      input.placeholder = prompt.slice(0, Math.max(0, chars)) || '\u200b';
      let wait = deleting ? 45 : 90;
      if (!deleting && chars >= prompt.length) {
        deleting = true;
        wait = 2400;
      } else if (deleting && chars <= 0) {
        deleting = false;
        index = (index + 1) % prompts.length;
        wait = 450;
      }
      timer = window.setTimeout(tick, wait);
    };
    const start = () => {
      window.clearTimeout(timer);
      if (idle()) timer = window.setTimeout(tick, 1500);
    };
    input.addEventListener('focus', () => {
      window.clearTimeout(timer);
      input.placeholder = original;
    });
    input.addEventListener('blur', start);
    phoneWidth.addEventListener('change', start);
    input.addEventListener('input', () => {
      if (input.value) window.clearTimeout(timer);
    });
    start();
  });

  /* Search bar product type filter ([data-search-scope], Commerce header):
   * "All categories" sends no filter; a chosen type narrows the results to
   * products of that type (Search & Discovery product type filter). */
  document.querySelectorAll('select[data-search-scope]').forEach((select) => {
    select.form?.addEventListener('formdata', (event) => {
      if (select.value) event.formData.set('type', 'product');
      else event.formData.delete(select.name);
    });
  });

  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);
  if (!customElements.get('disclosure-menu')) customElements.define('disclosure-menu', DisclosureMenu);
})();
